import React, { lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reportAPI } from 'api/report';
import { AssocArray } from 'tools/types';
import useComponentReady from 'components/report-content/hooks/useComponentReady';
import { Chart as HChart, RangeSelectorOptions, XAxisOptions, YAxisOptions } from 'highcharts';
import useOnApplyAccessMapChange from 'components/report-content/hooks/useOnApplyAccessMapChange';
import useOnMeasurementTime from 'components/report-content/hooks/useOnMeasurementTime';
import { Highcharts } from 'components/chart/Chart';
import useReportContentContext from 'components/report-content/hooks/useReportContentContext';
import ReportContentSkeletonPlaceholder from 'components/report-content/utils/ReportContentSkeletonPlaceholder';
import {
    KpiChartInitialDataResponse,
    ComponentSettingsChartDataQueries,
    DisplayMask,
    ComponentUpdateProps,
    ComponentSettingsKpiChart,
    applyChartOptionsForThumbnail,
    parseDate,
    formatValueFull,
    mysql_dateformat_to_highchart_dateformat,
    IKpiResults,
    contentSettingsToComponentProperties,
    IKpiAlertEvent,
} from '@report-content';
import { ChartOptions } from 'highcharts/highstock';
import TopControls from './parts/TopControls';
import { getKpiStoplight } from './series/getKpiStoplight';
import { getKpiAlert } from './series/getKpiAlert';
import { useTheme } from '@mui/material/styles';
import { getMeasurementSeries, IKpiMeasurePointData } from './series/getMeasurementSeries';
import { SeriesPrimaryId } from './series';
import TooltipContainer from 'components/chart/TooltipContainer';
import { ChartPoint } from 'components/metric/chart';
import { getMovingAverage } from './series/getMovingAverage';
import useBundleTranslation from 'i18n';
import { useTooltip } from './parts/useTooltip';
Highcharts.AST.allowedReferences.push('data:image/svg+xml;base64,');
const Chart = lazy(() => import('components/chart/Chart'));
type SeriesType = Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions | Highcharts.SeriesFlagsOptions;
function getFormattedValue(row: AssocArray<string>, field: string): string {
    return row[field + '-formatted'] ?? row[field];
}

export default function KpiChart({
    contentSettings,
    component,
    updateBlockSettings,
}: ComponentUpdateProps<ComponentSettingsKpiChart>) {
    const { t } = useBundleTranslation(['components/report-content']);
    const { filtersString } = useReportContentContext(() => {});
    const filtersStrRef = useRef(filtersString);
    useEffect(() => {
        filtersStrRef.current = filtersString;
        resetData();
    }, [filtersString]);
    // Last request to server was made with this data
    const [lastDataQueries, setDataQueries] = useState<ComponentSettingsChartDataQueries | null>(null);
    const isThumbnail = contentSettings.forScreenShot && window.location.href.includes('thumbnail');
    const dataQueryKey = `report_block_initial_${component.datasetRendererBlockComponentId}_${contentSettings.segmentValueId}`;
    const queryClient = useQueryClient();
    const resetData = () => queryClient.removeQueries({ queryKey: [dataQueryKey] });
    const { data: initialData } = useQuery<KpiChartInitialDataResponse, Error>({
        queryKey: [dataQueryKey],
        queryFn: () => {
            setDataQueries(component.settings.dataQueries);
            return reportAPI.getComponentData(
                component.settings,
                contentSettingsToComponentProperties(contentSettings),
                filtersStrRef.current,
            );
        },
    });

    useEffect(() => {
        // Remove data on unmount
        return () => resetData();
    }, []);

    const [data, setData] = useState<Array<AssocArray<string>>>([]);
    const [kpiData, setKpiData] = useState<Array<IKpiResults>>([]);
    const [alertsData, setAlertsData] = useState<Array<IKpiAlertEvent>>([]);
    // Reset all data on dataQueries change
    useEffect(() => {
        if (!initialData) {
            return;
        }
        if (
            lastDataQueries != null &&
            JSON.stringify(lastDataQueries) != JSON.stringify(component.settings.dataQueries)
        ) {
            resetData();
        }
    }, [component.settings.dataQueries]);

    useOnApplyAccessMapChange(contentSettings.applyAccessMap, resetData);
    useOnMeasurementTime(contentSettings.measurement_time, resetData);
    // Reset all data on Component Filter Change or Stoplight Change
    const watchStoplight = useMemo(() => {
        const result: any = structuredClone(component.settings.stoplight);
        delete result.stoplightColorForHighValues;
        delete result.stoplightColorForLowValues;
        return result;
    }, [component.settings.stoplight]);
    useEffect(() => {
        if (!initialData) {
            return;
        }
        resetData();
    }, [component.settings.groupFilter, component.settings.x_axis.field, JSON.stringify(watchStoplight)]);

    useEffect(() => {
        if (!initialData) {
            return;
        }
        setData(initialData.dataResults.table);
        setKpiData(initialData.kpiResults ?? []);
        setAlertsData(initialData.alertEvents ?? []);
    }, [initialData]);

    const { Tooltip, handlePointClick, handlePointMouseOver, handlePointMouseOut, pointToShow } = useTooltip();

    const xAxisSettingsTemplate = {
        enabled: false,
        lineWidth: 0,
        minorGridLineWidth: 0,
        lineColor: 'transparent',
        gridLineColor: 'transparent',
        labels: {
            enabled: false,
        },
        minorTickLength: 0,
        tickLength: 0,
        minPadding: 0,
        maxPadding: 0,
    };

    const yAxisSettingsTemplate = {
        opposite: false,
        minorTickLength: 0,
        tickLength: 0,
        minPadding: 0,
        maxPadding: 0,
        title: {
            text: '',
            useHTML: true,
        },
        labels: {
            // align: 'left',
        },
        plotLines: [
            {
                value: 0,
                width: 2,
                color: 'silver',
            },
        ],
    };

    const options: Omit<Highcharts.Options, 'chart rangeSelector xAxis yAxis series legend'> & {
        chart: ChartOptions;
        rangeSelector: RangeSelectorOptions;
        xAxis: XAxisOptions;
        yAxis: YAxisOptions;
        series: Array<SeriesType>;
        legend: Highcharts.LegendOptions;
    } = {
        chart: {
            animation: false,
            height: 600,
            zooming: {
                mouseWheel: {
                    enabled: false,
                },
            },
            spacingLeft: 16,
            spacingTop: 8,
        },
        tooltip: { enabled: false },
        legend: {
            enabled: !isThumbnail,
            useHTML: true,
            itemDistance: 10,
            //@ts-ignore
            custom: {
                scrollableLegendArea: {
                    minHeight: 120,
                },
            },
        },
        credits: { enabled: false },
        title: { text: '' },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        xAxis: xAxisSettingsTemplate,
        yAxis: yAxisSettingsTemplate,
        plotOptions: {
            series: {
                animation: false,
                turboThreshold: 0,
            },
            line: {
                stickyTracking: false,
                dataGrouping: {
                    enabled: false,
                },
            },
            area: { fillOpacity: 0.75 },
            flags: {
                clip: false,
                point: {
                    events: {
                        click: function () {
                            handlePointClick(this);
                        },
                        mouseOver: function () {
                            // Fix issue with moving flags on hover
                            //@ts-ignore
                            if (this._y) {
                                this.update({}, true);
                            }
                            handlePointMouseOver(this);
                        },
                        mouseOut: () => handlePointMouseOut(),
                    },
                },
            },
        },
        navigator: { enabled: !isThumbnail },
        scrollbar: { enabled: !isThumbnail },
        series: [],
    };

    const findDisplayMask = function (display_mask_id: string) {
        return contentSettings.displayMasks.find((m) => m.display_mask_id == display_mask_id);
    };
    //
    let dm: DisplayMask | undefined = undefined;
    const yAxisFormatter = function () {
        //@ts-ignore
        const v = this.value;
        return dm && 'date' === dm?.mask_type && dm?.date_format_string > ''
            ? Highcharts.dateFormat('%m/%d/%Y', v)
            : //@ts-ignore
              formatValueFull(v, dm);
    };

    const chart = useRef<HChart>();
    useComponentReady(chart.current);

    const theme = useTheme();
    const topPanelHeight = 52;

    const getSeries = () => {
        const innerData = structuredClone(data);
        let xColumn = component.settings.x_axis.field;

        const seriesOptions: Array<SeriesType> = [];
        // Add Measure Series
        const primary = component.settings.measureFields.find((s) => s.is_primary == 'Y');
        // Hide series with overlay_in_chart or overlay_in_tile enabled in chart or thumbnail respectively,
        // unless it's primary and no other series
        let alwaysShowPrimary = false;
        if (contentSettings.forScreenShot && !isThumbnail) {
            alwaysShowPrimary =
                component.settings.measureFields.findIndex((field) => field.overlay_in_chart == 'Y') == -1;
        } else if (isThumbnail) {
            alwaysShowPrimary =
                component.settings.measureFields.findIndex((field) => field.overlay_in_tile == 'Y') == -1;
        }
        component.settings.measureFields.forEach((field, index) => {
            let isVisible = true;
            if (contentSettings.forScreenShot) {
                isVisible =
                    (isThumbnail ? field.overlay_in_tile == 'Y' : field.overlay_in_chart == 'Y') ||
                    (field.is_primary == 'Y' && alwaysShowPrimary);
            }

            seriesOptions[index] = getMeasurementSeries(
                field,
                component.settings.display_label == 'Y',
                index,
                isVisible,
            );
            seriesOptions[index].point = {
                events: {
                    click: function () {
                        handlePointClick(this);
                    },
                    mouseOver: function () {
                        //@ts-ignore
                        handlePointMouseOver(this, this.series.id);
                    },
                    mouseOut: function () {
                        handlePointMouseOut();
                    },
                },
            };
        });
        let maxY = Number.NEGATIVE_INFINITY,
            minY = Number.POSITIVE_INFINITY;
        innerData.forEach((row) => {
            const xVal = row[xColumn];
            component.settings.measureFields.forEach((field, fieldIndex) => {
                const val = parseFloat(row[field.reference_name]);
                if (isNaN(val) || null == xVal) {
                    return;
                }
                if (val > maxY) {
                    maxY = val;
                }
                if (val < minY) {
                    minY = val;
                }

                seriesOptions[fieldIndex]!.data!.push({
                    x: parseDate(xVal).getTime(),
                    y: val,
                    custom: {
                        color: field.color,
                        name_f: field.column_name,
                        x_f: getFormattedValue(row, xColumn),
                        y_f: getFormattedValue(row, field.reference_name),
                    } as IKpiMeasurePointData,
                });
            });
        });
        // Add Stoplight Series
        if (component.settings.stoplight.includeStoplight == 'Y') {
            const stoplight = getKpiStoplight(
                component.settings.stoplight,
                kpiData,
                minY,
                maxY,
                'calendar_date',
                primary?.column_name ?? '',
                t,
            );
            seriesOptions.push(stoplight[0]);
            seriesOptions.push(stoplight[1]);
        }
        // Add Moving Average Series
        if (component.settings.kpiVariance.includeKpiVariance == 'Y') {
            const deviation = getMovingAverage(component.settings.kpiVariance, kpiData, 'calendar_date', t, theme);
            seriesOptions.push(deviation);
        }
        // Add Alert Series
        const primarySeries = seriesOptions.find((s) => s.id == SeriesPrimaryId) ?? seriesOptions[0];
        const alertSeries = getKpiAlert(
            alertsData,
            primarySeries as Highcharts.SeriesLineOptions,
            component.settings.x_axis.field,
            theme,
        );
        if (alertSeries?.data?.length) {
            seriesOptions.push(alertSeries);
            // Fix issue with first hover on alert points
            setTimeout(() => {
                if (chart.current) {
                    try {
                        chart.current.redraw();
                    } catch (e) {}
                }
            }, 1000);
        }
        // Show marker when series has only 1 point
        seriesOptions.forEach((s) => {
            if (s.data?.length == 1) {
                (s as Highcharts.SeriesLineOptions).marker = { enabled: true, radius: 4 };
            }
        });
        // Disable legend if only 1 series
        if (seriesOptions.filter((s) => s.showInLegend !== false).length == 1) {
            options.legend.enabled = false;
        }
        return seriesOptions;
    };

    const chartOptions = useMemo(() => {
        if (isThumbnail) {
            applyChartOptionsForThumbnail(options);
        } else {
            options.chart.height = component.settings.height - topPanelHeight;
        }

        if (!isThumbnail && component.settings.x_axis.title) {
            options.xAxis.title = { text: component.settings.x_axis.title, useHTML: true };
        }

        const dmXAxis = findDisplayMask(component.settings.x_axis.display_mask_id);
        options.xAxis.labels = {
            enabled: !isThumbnail,
            formatter: dmXAxis
                ? function (): string {
                      const v = Number(this.value);
                      if (dmXAxis && 'date' === dmXAxis.mask_type && dmXAxis.date_format_string > '') {
                          return isNaN(v)
                              ? (this.value as string)
                              : Highcharts.dateFormat(mysql_dateformat_to_highchart_dateformat(dmXAxis), v);
                      }
                      return Number(this.value) == this.value
                          ? Highcharts.dateFormat('%m/%d/%Y', v)
                          : (this.value as string);
                  }
                : undefined,
        };

        if (component.settings?.y_axis?.length) {
            options.yAxis!.title!.text = component.settings.y_axis[0].title;
            options.yAxis!.labels!.formatter = yAxisFormatter;
            dm = findDisplayMask(component.settings.measureFields[0].display_mask_id);
            if (dm && 'date' == dm.mask_type && dm.date_format_string > '') {
                options.yAxis.type = 'datetime';
            }

            if ('Y' == component.settings.display_label) {
                let offsetY = -5;
                for (let i = 0; i < component.settings.measureFields.length; i++) {
                    if ('column' != component.settings.measureFields[i].display_type) {
                        offsetY = -15;
                        break;
                    }
                }
                options.yAxis.stackLabels = {
                    enabled: true,
                    y: offsetY,
                    style: {
                        fontWeight: 'bold',
                        color: 'black',
                    },
                };
            }
        }

        options.series = getSeries();
        return options;
    }, [data, kpiData, JSON.stringify(component.settings)]);

    const [selectedRange, setSelectedRange] = useState<string>('all');

    return initialData ? (
        <div style={{ overflow: 'hidden', maxHeight: component.settings.height + 'px', position: 'relative' }}>
            {!contentSettings.forScreenShot && (
                <TopControls
                    chart={chart.current}
                    selectedRange={selectedRange}
                    onChange={(range: string) => setSelectedRange(range)}
                />
            )}
            <Chart
                immutable={true}
                chartOptions={chartOptions}
                afterChartCreated={(c: HChart) => (chart.current = c)}
            />
            {chart.current && Tooltip && (
                <TooltipContainer point={{ ...pointToShow, metadata: {} } as ChartPoint} chart={chart.current}>
                    {(closeComponent) => Tooltip}
                </TooltipContainer>
            )}
        </div>
    ) : (
        <ReportContentSkeletonPlaceholder height={component.settings.height} />
    );
}
